import { Injectable, inject } from '@angular/core';
import { ExportGasXActions } from '@app/home/features/export-gasx/store';
import { TopologyActions } from '@app/home/features/topology/store';
import { ManualGasXTransfert } from '@core/models/manual-gasx-transfert.model';
import { Piston } from '@core/models/piston.model';
import { SimulationResult } from '@core/models/simulation-metadata.model';
import { Topology } from '@core/models/topology.model';
import { environment } from '@env/environment';
import * as signalR from '@microsoft/signalr';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { CoreActions, fromCore } from '../store';
import { PathGraphRequest, PathGraphResponse } from '@app/home/features/gas-block/models/path-request.model';
import { GasBlockActions } from '@app/home/features/gas-block/store';
import { DashboardActions } from '@app/home/features/dashboard/store';
import { ReportActions } from '@app/home/features/report/store';
import { Error } from '@app/home/features/error/models/error.model';
import { ErrorActions } from '@app/home/features/error/store';
import { TimestreamMetric } from '@app/home/models/metric.model';
import { ProcessStatus } from '@core/models/processStatus.model';
import { ExtractionResponse } from '@app/home/features/topology/models/extraction.model';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private store = inject(Store);

  private hubUrl: string = environment.urlHub + '/smg';
  private connection: signalR.HubConnection;
  public connected = false;

  public async connect(): Promise<void> {
    this.connection = new signalR.HubConnectionBuilder()
      .configureLogging(signalR.LogLevel.None)
      .withAutomaticReconnect(this.retryPolicy())
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => this.getToken(),
        skipNegotiation: environment.env === 'local' ? false : true,
        transport: environment.env === 'local' ? null : signalR.HttpTransportType.WebSockets
      })
      .build();

    // TODO: il serait mieux de gérer ces events dans les modules concernés au lieu du core

    this.connection.on('RefreshDashboard', (dashboardId: string) => {
      this.store.dispatch(DashboardActions.refreshDashboard({ dashboardId }));
    });

    this.connection.on('RemoveDashboard', (dashboardId: string) => {
      this.store.dispatch(DashboardActions.removeDashboardSuccess({ dashboardId }));
    });

    this.connection.on('RefreshDashboardList', () => {
      this.store.dispatch(DashboardActions.refreshDashboardList());
    });

    this.connection.on('RefreshTopologies', (topologies: Array<Topology>) => {
      this.store.dispatch(TopologyActions.loadTopologiesSuccess({ topologies }));
    });

    this.connection.on('SendPistonPositions', (pistons: Array<Piston>) => {
      this.store.dispatch(TopologyActions.updatePistons({ pistons }));
    });

    this.connection.on('UpdateSimulationResults', (simulationResult: SimulationResult) => {
      this.store.dispatch(CoreActions.updateLatestSimulationResult({ simulationResult }));
    });

    this.connection.on('UpdateManualGasXTransfert', (manualGasXTransfert: ManualGasXTransfert) => {
      this.store.dispatch(ExportGasXActions.updateLatestManualGasXTransfert({ manualGasXTransfert }));
    });

    this.connection.on('GraphExportData', (data: Array<PathGraphResponse>) => {
      this.store.dispatch(GasBlockActions.resultExport({ data }));
    });

    this.connection.on('GenerateReport', (file: Blob) => {
      this.store.dispatch(ReportActions.generateReportReceived({ file }));
    });

    this.connection.on('PreviewReport', (data: Array<TimestreamMetric>) => {
      this.store.dispatch(ReportActions.loadPreview({ data }));
    });

    this.connection.on('SyncNewError', (error: Error) => {
      this.store.dispatch(ErrorActions.syncNewError({ error }));
    });

    this.connection.on('GenerateExtraction', (extractionResult: ExtractionResponse) => {
      this.store.dispatch(TopologyActions.generateExtractionReceived({ extractionResult }));
    });

    this.connection.on('UpdatePastDataStatus', (currentStep: number, totalSteps: number) => {
      const pastDataStatus: ProcessStatus = { currentStep, totalSteps };
      this.store.dispatch(CoreActions.updatePastDataStatus({ pastDataStatus }));
    });

    this.connection.on('UpdateExtractionStatus', (currentStep: number, totalSteps: number) => {
      const variationStatus: ProcessStatus = { currentStep, totalSteps };
      this.store.dispatch(CoreActions.updateVariationStatus({ variationStatus }));
    });

    this.connection.onreconnected(() => {
      this.store.dispatch(CoreActions.reconnect());
    });

    return this.connection
      .start()
      .then(done => {
        this.connected = true;
        console.log('signalR started');
        this.getEmail().then(email => {
          this.AddToCustomGroup(email);
        });
      })
      .catch(e => {});
  }

  public disconnect(): void {
    if (this.connection) {
      this.connection.stop().catch(e => {});
    }
  }

  public AddToCustomGroup(custom: string): void {
    if (this.connected) {
      this.connection.invoke('AddToGroup', custom).catch(e => {});
    } else {
      setTimeout(() => {
        this.AddToCustomGroup(custom);
      }, 100);
    }
  }

  public RemoveFromCustomGroup(custom: string): void {
    this.connection.invoke('RemoveFromGroup', custom).catch(e => {});
  }

  public requestRefreshTopologies(): void {
    this.connection.invoke('RefreshTopologies').catch(e => {});
  }

  // public requestRefreshDashboard(dashboardId: string): void {
  //   this.connection.invoke('RefreshDashboard', dashboardId).catch(e => {});
  // }

  public requestGraphExportData(request: PathGraphRequest): void {
    this.connection.invoke('GraphExportData', request).catch(e => {});
  }

  private getToken(): Promise<string> {
    return firstValueFrom(this.store.select(fromCore.getUser).pipe(map(user => user.bearer)));
  }

  private getEmail(): Promise<string> {
    return firstValueFrom(this.store.select(fromCore.getUser).pipe(map(user => user.email)));
  }

  private retryPolicy(): signalR.IRetryPolicy {
    return {
      nextRetryDelayInMilliseconds: (retryContext: signalR.RetryContext) => 10000
    };
  }
}
